import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Breadcrumb from '../../components/common/Breadcrumb';
import toast from 'react-hot-toast';
import {
    IconChevronRight, IconPlus, IconX, IconCheck,
    IconLoader, IconSearch, IconUserCheck, IconUsers, IconBriefcase,
    IconMail, IconSend, IconCalendar, IconCircleCheck, IconLock,
} from '@tabler/icons-react';
import {
    use360Lookup,
    useCreate360Request,
    useActivate360Request,
} from '../../hooks/usePerformance';
import { useAuth } from '../../contexts/AuthContext';

const RATER_TABS = [
    { key: 'supervisor', label: 'Supervisor',     endpoint: 'supervisors', icon: IconUserCheck },
    { key: 'supervisee', label: 'Direct Reports', endpoint: 'supervisees', icon: IconUsers     },
    { key: 'colleague',  label: 'Colleagues',     endpoint: 'employees',   icon: IconBriefcase },
    { key: 'external',   label: 'External',        endpoint: null,          icon: IconMail      },
];

const RATER_COLORS = {
    self:       { bg: 'bg-primary/10',  text: 'text-primary',  label: 'Self'          },
    supervisor: { bg: 'bg-info/10',     text: 'text-info',     label: 'Supervisor'    },
    supervisee: { bg: 'bg-success/10',  text: 'text-success',  label: 'Direct Report' },
    colleague:  { bg: 'bg-warning/10',  text: 'text-warning',  label: 'Colleague'     },
    external:   { bg: 'bg-purple/10',   text: 'text-purple',   label: 'External'      },
};

const AVATAR_COLORS = ['#6366f1', '#10b981', '#f59e0b', '#a855f7', '#06b6d4', '#3b82f6', '#f43f5e'];
function avatarColor(str = '') {
    return AVATAR_COLORS[(str.charCodeAt(0) || 0) % AVATAR_COLORS.length];
}
function initials(name = '') {
    return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2) || '?';
}

const STEPS = [
    { id: 1, label: 'Cycle Info',  icon: IconCalendar },
    { id: 2, label: 'Nominations', icon: IconUsers    },
];

function PersonRow({ person, added, onAdd }) {
    return (
        <div className="flex items-center gap-8 px-10 py-6 rounded border border-border bg-bg-light hover:bg-bg-dark/60 transition-colors">
            <div className="flex-grow min-w-0">
                <p className="text-[12px] font-semibold text-text-primary truncate leading-tight">{person.full_name}</p>
                {(person.job_title || person.email) && (
                    <p className="text-[10px] text-text-secondary truncate leading-none mt-1">{person.job_title || person.email}</p>
                )}
            </div>
            <button
                onClick={() => onAdd(person)}
                disabled={added}
                className={`flex-shrink-0 flex items-center gap-3 px-7 py-3 rounded text-[10px] font-bold border transition-all ${
                    added
                        ? 'bg-success/10 text-success border-success/30 cursor-default'
                        : 'bg-primary/10 text-primary border-primary/30 hover:bg-primary hover:text-white'
                }`}
            >
                {added ? <><IconCheck size={10} /> Added</> : <><IconPlus size={10} /> Add</>}
            </button>
        </div>
    );
}

export default function Feedback360New() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const orgId = localStorage.getItem('orgId') || '';

    const [step, setStep] = useState(1);
    const [form, setForm] = useState({
        cycle_name: '', cycle_start_date: '', cycle_end_date: '', deadline: '',
    });
    const [nominations, setNominations] = useState([]);
    const [activeTab, setActiveTab]     = useState('supervisor');
    const [search, setSearch]           = useState('');
    const [extForm, setExtForm]         = useState({ external_name: '', external_email: '' });

    const createRequest  = useCreate360Request();
    const activateRequest = useActivate360Request();

    useEffect(() => {
        if (user?.employeeId) {
            setNominations([{
                rater_type: 'self', rater_employee_id: user.employeeId,
                display_name: user.name || 'You', _key: 'self',
            }]);
        }
    }, [user]);

    const excludeIds   = nominations.filter(n => n.rater_employee_id).map(n => n.rater_employee_id);
    const currentTab   = RATER_TABS.find(t => t.key === activeTab);
    const { data: lookupData, isFetching: lookupFetching } = use360Lookup(
        step === 2 ? currentTab?.endpoint : null, orgId, search, excludeIds,
    );
    const lookupResults = lookupData?.results || [];

    const addEmployee = (person) => {
        if (nominations.some(n => n.rater_employee_id === person.id)) return;
        setNominations(prev => [...prev, {
            rater_type: activeTab, rater_employee_id: person.id,
            display_name: person.full_name, job_title: person.job_title, _key: person.id,
        }]);
        setSearch('');
    };

    const addExternal = () => {
        const { external_name, external_email } = extForm;
        if (!external_name.trim() || !external_email.trim()) {
            toast.error('Enter both name and email for the external rater.');
            return;
        }
        setNominations(prev => [...prev, {
            rater_type: 'external', external_name: external_name.trim(),
            external_email: external_email.trim(), display_name: external_name.trim(),
            _key: `ext-${external_email.trim()}`,
        }]);
        setExtForm({ external_name: '', external_email: '' });
    };

    const removeNomination = (key) => {
        setNominations(prev => prev.filter(n => n._key !== key));
    };

    const validateStep1 = () => {
        if (!form.cycle_name.trim())  { toast.error('Cycle name is required.');  return false; }
        if (!form.cycle_start_date)   { toast.error('Start date is required.');  return false; }
        if (!form.cycle_end_date)     { toast.error('End date is required.');    return false; }
        if (!form.deadline)           { toast.error('Deadline is required.');    return false; }
        return true;
    };

    const buildPayload = () => ({
        ...form,
        nominations: nominations.map(n => n.rater_type === 'external'
            ? { rater_type: 'external', external_name: n.external_name, external_email: n.external_email }
            : { rater_type: n.rater_type, rater_employee_id: n.rater_employee_id }
        ),
    });

    const handleSaveDraft = async () => {
        if (!validateStep1()) return;
        try {
            await createRequest.mutateAsync({ data: buildPayload() });
            toast.success('Saved as draft.');
            navigate('/performance/360-feedback');
        } catch {}
    };

    const handleSendRequests = async () => {
        if (!validateStep1()) return;
        if (nominations.length < 2) {
            toast.error('Add at least one rater besides yourself.');
            return;
        }
        try {
            const created = await createRequest.mutateAsync({ data: buildPayload() });
            await activateRequest.mutateAsync({ id: created.id });
            toast.success('360 review activated! Emails sent to all nominees.');
            navigate(`/performance/360-feedback/request/${created.id}`);
        } catch {}
    };

    const isBusy = createRequest.isPending || activateRequest.isPending;

    return (
        <div className="pb-30">
            <div className="mb-30">
                <Breadcrumb title="New 360 Review" path="Performance" />
            </div>

            {/* Step indicator */}
            <div className="flex gap-20 overflow-x-auto pb-5 mb-30">
                {STEPS.map((s, index) => {
                    const Icon        = s.icon;
                    const isActive    = step === s.id;
                    const isCompleted = step > s.id;
                    return (
                        <div
                            key={s.id}
                            onClick={() => { if (s.id === 1 || (s.id === 2 && validateStep1())) setStep(s.id); }}
                            className={`flex-1 min-w-[160px] p-20 rounded-lg border transition-all cursor-pointer shadow-light
                                ${isActive    ? 'bg-bg-dark border-primary shadow-medium ring-1 ring-primary/20'
                                : isCompleted ? 'bg-bg-dark border-success/30 opacity-80'
                                              : 'bg-bg-dark border-border hover:border-primary/50'}`}
                        >
                            <div className="flex items-center justify-between mb-15">
                                <div className={`p-10 rounded-lg transition-all ${isActive ? 'bg-primary text-white shadow-lg shadow-primary/20' : 'bg-bg-light text-text-secondary'}`}>
                                    <Icon size={18} />
                                </div>
                                {isCompleted && (
                                    <div className="w-20 h-20 rounded-full bg-success flex items-center justify-center">
                                        <IconCircleCheck size={14} className="text-white" />
                                    </div>
                                )}
                            </div>
                            <div className={`text-[10px] font-bold uppercase tracking-widest mb-2 ${isActive ? 'text-primary' : 'text-text-secondary'}`}>Step {s.id}</div>
                            <div className={`text-sm font-medium ${isActive ? 'text-text-primary' : 'text-text-secondary'}`}>{s.label}</div>
                        </div>
                    );
                })}
            </div>

            {/* ── STEP 1: Cycle Info ── */}
            {step === 1 && (
                <div className="bg-bg-dark rounded-lg border border-border shadow-light overflow-hidden">
                    <div className="p-20 border-b border-border bg-bg-light/30">
                        <h2 className="text-[15px] font-medium text-text-primary uppercase tracking-widest">Cycle Information</h2>
                        <p className="text-[10px] font-bold uppercase tracking-widest text-text-secondary opacity-70 mt-2">
                            Define the scope and timeline of this review cycle
                        </p>
                    </div>
                    <div className="p-20">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-20">
                            <div className="md:col-span-2 form-group">
                                <label className="block text-[10px] font-bold uppercase tracking-widest text-text-secondary mb-8">Cycle Name *</label>
                                <input
                                    type="text"
                                    placeholder="e.g. Annual Performance Review 2025"
                                    value={form.cycle_name}
                                    onChange={e => setForm(p => ({ ...p, cycle_name: e.target.value }))}
                                />
                            </div>
                            <div className="form-group">
                                <label className="block text-[10px] font-bold uppercase tracking-widest text-text-secondary mb-8">Start Date *</label>
                                <input type="date" value={form.cycle_start_date} onChange={e => setForm(p => ({ ...p, cycle_start_date: e.target.value }))} />
                            </div>
                            <div className="form-group">
                                <label className="block text-[10px] font-bold uppercase tracking-widest text-text-secondary mb-8">End Date *</label>
                                <input type="date" value={form.cycle_end_date} onChange={e => setForm(p => ({ ...p, cycle_end_date: e.target.value }))} />
                            </div>
                            <div className="form-group">
                                <label className="block text-[10px] font-bold uppercase tracking-widest text-text-secondary mb-8">Response Deadline *</label>
                                <input type="date" value={form.deadline} onChange={e => setForm(p => ({ ...p, deadline: e.target.value }))} />
                            </div>
                        </div>
                        <div className="flex justify-end mt-20 pt-20 border-t border-border">
                            <button onClick={() => { if (validateStep1()) setStep(2); }} className="btn btn-primary">
                                Next: Add Nominations <IconChevronRight size={15} />
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── STEP 2: Nominations ── */}
            {step === 2 && (
                <>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-20">

                    {/* Left — search panel */}
                    <div className="bg-bg-dark rounded-lg border border-border shadow-light overflow-hidden">
                        <div className="p-20 border-b border-border bg-bg-light/30">
                            <h2 className="text-[15px] font-medium text-text-primary uppercase tracking-widest">Add Raters</h2>
                            <p className="text-[10px] font-bold uppercase tracking-widest text-text-secondary opacity-70 mt-2">
                                Search and select people to nominate
                            </p>
                        </div>
                        <div className="p-20">
                            {/* Rater type tabs */}
                            <div className="flex flex-wrap gap-4 mb-15">
                                {RATER_TABS.map(t => (
                                    <button
                                        key={t.key}
                                        onClick={() => { setActiveTab(t.key); setSearch(''); }}
                                        className={`px-10 py-4 rounded text-[10px] font-bold uppercase tracking-widest border transition-all ${
                                            activeTab === t.key
                                                ? 'bg-primary text-white border-primary'
                                                : 'bg-bg-light text-text-secondary border-border hover:border-primary/40 hover:text-text-primary'
                                        }`}
                                    >
                                        {t.label}
                                    </button>
                                ))}
                            </div>

                            {/* Supervisor */}
                            {activeTab === 'supervisor' && (
                                lookupFetching ? (
                                    <div className="py-15 text-center text-text-secondary"><IconLoader className="animate-spin inline-block" size={16} /></div>
                                ) : lookupResults.length === 0 ? (
                                    <p className="text-[11px] text-text-secondary italic">No supervisor found.</p>
                                ) : (
                                    <div className="flex flex-col gap-5">
                                        {lookupResults.map(person => (
                                            <PersonRow
                                                key={person.id}
                                                person={person}
                                                added={nominations.some(n => n.rater_employee_id === person.id)}
                                                onAdd={addEmployee}
                                            />
                                        ))}
                                    </div>
                                )
                            )}

                            {/* Supervisee / Colleague */}
                            {(activeTab === 'supervisee' || activeTab === 'colleague') && (
                                <div>
                                    <div className="form-group relative mb-12">
                                        <IconSearch size={13} className="absolute top-1/2 -translate-y-1/2 text-text-secondary pointer-events-none" style={{ left: '10px' }} />
                                        <input
                                            type="text"
                                            placeholder={`Search ${activeTab === 'supervisee' ? 'direct reports' : 'colleagues'}...`}
                                            value={search}
                                            onChange={e => setSearch(e.target.value)}
                                            style={{ paddingLeft: '28px' }}
                                        />
                                        {lookupFetching && <IconLoader size={13} className="absolute top-1/2 -translate-y-1/2 animate-spin text-text-secondary" style={{ right: '10px' }} />}
                                    </div>
                                    {search.length < 2 ? (
                                        <p className="text-[11px] text-text-secondary italic">Type at least 2 characters to search.</p>
                                    ) : lookupResults.length === 0 && !lookupFetching ? (
                                        <p className="text-[11px] text-text-secondary italic">No results found.</p>
                                    ) : (
                                        <div className="flex flex-col gap-5">
                                            {lookupResults.map(person => (
                                                <PersonRow
                                                    key={person.id}
                                                    person={person}
                                                    added={nominations.some(n => n.rater_employee_id === person.id)}
                                                    onAdd={addEmployee}
                                                />
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* External */}
                            {activeTab === 'external' && (
                                <div>
                                    <div className="form-group mb-5">
                                        <label className="block text-[10px] font-bold uppercase tracking-widest text-text-secondary mb-3">Full Name</label>
                                        <input type="text" placeholder="John Smith" value={extForm.external_name} onChange={e => setExtForm(p => ({ ...p, external_name: e.target.value }))} />
                                    </div>
                                    <div className="form-group mb-10">
                                        <label className="block text-[10px] font-bold uppercase tracking-widest text-text-secondary mb-3">Email Address</label>
                                        <input type="email" placeholder="john@company.com" value={extForm.external_email} onChange={e => setExtForm(p => ({ ...p, external_email: e.target.value }))} />
                                    </div>
                                    <button onClick={addExternal} className="btn btn-primary">
                                        <IconPlus size={14} /> Add External Rater
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Right — nominations list */}
                    <div className="bg-bg-dark rounded-lg border border-border shadow-light overflow-hidden self-start">
                        <div className="p-20 border-b border-border bg-bg-light/30 flex items-center justify-between">
                            <div>
                                <h2 className="text-[15px] font-medium text-text-primary uppercase tracking-widest">Nominations</h2>
                                <p className="text-[10px] font-bold uppercase tracking-widest text-text-secondary opacity-70 mt-2">
                                    {nominations.length} rater{nominations.length !== 1 ? 's' : ''} added
                                </p>
                            </div>
                        </div>

                        {nominations.length === 0 ? (
                            <div className="p-15 text-center text-text-secondary">
                                <p className="text-[11px] italic">No nominations added yet.</p>
                            </div>
                        ) : (
                            <div className="divide-y divide-border">
                                {nominations.map(n => {
                                    const color = RATER_COLORS[n.rater_type] || RATER_COLORS.colleague;
                                    return (
                                        <div key={n._key} className="flex items-center gap-8 px-15 py-10">
                                            <div className="w-6 h-6 rounded-full flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0" style={{ backgroundColor: avatarColor(n.display_name) }}>
                                                {initials(n.display_name)}
                                            </div>
                                            <div className="flex-grow min-w-0">
                                                <p className="text-[12px] font-semibold text-text-primary truncate leading-tight">{n.display_name}</p>
                                                {(n.job_title || n.external_email) && (
                                                    <p className="text-[10px] text-text-secondary truncate leading-none mt-1">{n.job_title || n.external_email}</p>
                                                )}
                                            </div>
                                            <div className="flex items-center gap-6 flex-shrink-0">
                                                <span className={`text-[9px] font-bold uppercase tracking-widest px-6 py-2 rounded-full ${color.bg} ${color.text}`}>
                                                    {color.label}
                                                </span>
                                                {n._key === 'self' ? (
                                                    <div className="w-20 h-20 flex items-center justify-center text-text-secondary/50" title="Cannot remove self">
                                                        <IconLock size={11} />
                                                    </div>
                                                ) : (
                                                    <button
                                                        onClick={() => removeNomination(n._key)}
                                                        className="w-20 h-20 rounded-full flex items-center justify-center text-text-secondary hover:bg-danger/10 hover:text-danger transition-colors"
                                                    >
                                                        <IconX size={11} />
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {/* Actions — full width */}
                    <div className="lg:col-span-2 flex items-center justify-end gap-10 flex-wrap">
                        <button onClick={handleSaveDraft} disabled={isBusy} className="btn btn-outline-secondary">
                            {createRequest.isPending && !activateRequest.isPending && <IconLoader className="animate-spin" size={14} />}
                            Save as Draft
                        </button>
                        <button onClick={handleSendRequests} disabled={isBusy} className="btn btn-primary">
                            {isBusy ? <IconLoader className="animate-spin" size={14} /> : <IconSend size={14} />}
                            Send Requests
                        </button>
                    </div>
                </div>
                </>
            )}
        </div>
    );
}
